import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Target, AlertCircle, Sparkles, MapPin, GraduationCap, Trophy, Zap, Play, CheckCircle2, Circle, Clock } from 'lucide-react';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { Link } from 'react-router-dom';
import type { UserProfile, Bio } from '@/types/userProfile';
import type { LearningPlan, ActivePlanDashboardResponse } from '@/types/learningManagement';
import type { CoachGoalsResponse } from '@/types/coach';
import { cn } from '@/lib/utils';
import { getLearningPathDayDisplayLabel, isGenericLabel, stripLearningJourneyPrefix } from '@/utils/learningPathDisplayUtils';
import { getWinsAndDays } from '@/pages/coach/coachWinsUtils';
import { skillApi } from '@/services/skillKnowledgeApi';
import { Button } from '@/components/ui/Button';

interface CoachJourneySummaryProps {
  profile: UserProfile | null;
  learningPlan: LearningPlan | null;
  /** Optional goals (shortTermGoals) to use as labels for "Goal 1", "Goal 2", etc. */
  goals?: CoachGoalsResponse | null;
  /** Compact layout for context panel; default false for welcome area */
  compact?: boolean;
  className?: string;
  /** When set, each lesson day shows a Start button that sends this message into the coach chat */
  onStartLesson?: (day: number, label: string) => void;
  /** When set, each in-progress day shows a Complete button that marks that day's lesson complete */
  onCompleteLesson?: (day: number, label: string) => void;
  /** Dashboard with progress (daysCompleted, currentDayNumber) to show day status */
  dashboard?: ActivePlanDashboardResponse | null;
  /** Day number -> lesson title from API; when set, day labels use this instead of goals/cached data */
  lessonTitlesByDay?: Record<number, string>;
  /** Active wins from GET .../wins; when set, these are shown first (primaryWin = wins[0]). */
  activeWins?: { id?: string; name: string; description?: string }[] | null;
  /** Primary win (first of activeWins); use for prominent display when available. */
  primaryWin?: { name: string; description?: string; summary?: string } | null;
}

export type DayStatus = 'completed' | 'in_progress' | 'not_started' | 'overdue';

/** Derive day status from backend completion tracking (no time-based inference). */
function getDayStatus(
  dayNumber: number,
  dashboard: ActivePlanDashboardResponse | null | undefined
): DayStatus {
  if (!dashboard) return 'not_started';
  const completed = dashboard.completedDayNumbers ?? [];
  const overdue = dashboard.overdueDayNumbers ?? [];
  const current = dashboard.currentDayNumber ?? 1;
  if (completed.includes(dayNumber)) return 'completed';
  if (overdue.includes(dayNumber)) return 'overdue';
  if (dayNumber === current) return 'in_progress';
  return 'not_started';
}

/** Returns true if string looks like an ID (UUID or long hex) rather than human-readable text. */
function looksLikeId(s: string): boolean {
  if (!s || s.length < 10) return false;
  return /^[0-9a-f-]{20,}$/i.test(s) || /^[0-9a-f]{24}$/i.test(s);
}

/** Extract skill items from addressingSkillSets. Returns { name, description? } and IDs needing resolution. */
function extractSkills(skillSets: unknown[]): {
  items: { name: string; description?: string }[];
  idsToResolve: string[];
} {
  const items: { name: string; description?: string }[] = [];
  const idsToResolve: string[] = [];
  for (const s of skillSets) {
    const raw = s as Record<string, unknown>;
    const name = String(raw.skillName ?? raw.skill_name ?? raw.name ?? '').trim();
    const desc = raw.description ?? raw.summary;
    const description = typeof desc === 'string' ? desc : undefined;
    const id = String(raw.skillId ?? raw.skill_id ?? raw.id ?? '').trim();
    if (name) {
      items.push({ name, description });
    } else if (id && !looksLikeId(id)) {
      items.push({ name: id, description });
    } else if (id) {
      idsToResolve.push(id);
    }
  }
  return { items, idsToResolve };
}

/** Flatten daily contents: returns day, title, description, and displayLabel (description preferred when title is generic). */
function getDayItems(learningPlan: LearningPlan | null): { day: number; title: string; description?: string; displayLabel: string }[] {
  const mods = learningPlan?.learningModules ?? [];
  const result: { day: number; title: string; description?: string; displayLabel: string }[] = [];
  let dayNum = 1;
  for (const mod of mods) {
    const contents = mod.dailyContents ?? [];
    for (const d of contents) {
      const raw = d as Record<string, unknown>;
      const day = (raw.dayNumber ?? raw.day ?? dayNum) as number;
      const title = (raw.title ?? `Day ${day}`) as string;
      const description = typeof raw.description === 'string' ? raw.description.trim() : undefined;
      const displayLabel = getLearningPathDayDisplayLabel(raw, day, 60);
      // Use backend-provided day when available. This ensures the Start/Complete
      // actions hit the correct user-lesson day endpoints.
      result.push({ day, title, description, displayLabel });
      dayNum += 1;
    }
  }
  return result;
}

/**
 * Displays the user's learning path, wins they're chasing, and pain points they're solving.
 * Uses dynamic "X wins in Y days" from getWinsAndDays (profile, plan, dashboard).
 */
/** Resolve "Goal N" to shortTermGoal title when available. */
function resolveGoalLabel(label: string, index: number, goals: CoachGoalsResponse | null | undefined): string {
  const resolved = goals?.shortTermGoals?.[index]?.title ?? goals?.shortTermGoals?.[index]?.description;
  if (resolved && typeof resolved === 'string' && resolved.trim()) return resolved.trim();
  return label;
}

function DayStatusBadge({ status }: { status: DayStatus }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 text-[10px] font-medium">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        Completed
      </span>
    );
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-medium">
        <Clock className="h-3 w-3 shrink-0" />
        Catch up
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-medium">
        <BrandedSpinner sizeClassName="h-3 w-3" className="shrink-0" />
        In progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/80 text-muted-foreground px-1.5 py-0.5 text-[10px] font-medium">
      <Circle className="h-3 w-3 shrink-0" />
      Not started
    </span>
  );
}

export const CoachJourneySummary: React.FC<CoachJourneySummaryProps> = ({
  profile,
  learningPlan,
  goals,
  compact = false,
  className,
  onStartLesson,
  onCompleteLesson,
  dashboard,
  lessonTitlesByDay,
  activeWins,
  primaryWin,
}) => {
  const bio = profile?.bio as Bio | undefined;
  const bioPainPoints = bio?.painPoints ?? [];
  const planPainPoints = learningPlan?.painPoints ?? [];
  const painPoints = [
    ...bioPainPoints.map((p) => ({ id: p.painPointId, desc: p.description || p.painPointId })),
    ...planPainPoints.map((p) => {
      const raw = p as unknown as Record<string, unknown>;
      const desc = String(raw.painPointDescription ?? raw.pain_point_description ?? raw.painPointId ?? raw.pain_point_id ?? '');
      return { id: String(raw.painPointId ?? raw.pain_point_id ?? ''), desc };
    }),
  ].filter((p) => p.desc);
  const skills = learningPlan?.addressingSkillSets ?? [];
  const { items: initialSkillItems, idsToResolve: planSkillIdsToResolve } = extractSkills(
    Array.isArray(skills) ? skills : []
  );
  const profileSkills = (bio?.skills ?? []) as { skillId?: string; name?: string }[];
  const profileSkillIdsToResolve = profileSkills
    .filter((s) => s.skillId && looksLikeId(s.skillId) && !(s.name && s.name.trim()))
    .map((s) => s.skillId!);
  const skillIdsToResolve = [...new Set([...planSkillIdsToResolve, ...profileSkillIdsToResolve])];
  const dayItems = getDayItems(learningPlan);

  if (import.meta.env.DEV && dayItems.length > 0) {
    const shortTermCount = goals?.shortTermGoals?.length ?? 0;
    const labelsFromPlan = dayItems.map((d) => ({ day: d.day, label: d.displayLabel, generic: isGenericLabel(d.displayLabel) }));
    const genericCount = labelsFromPlan.filter((l) => l.generic).length;
    console.log('[CoachJourneySummary] Day labels source:', {
      totalDays: dayItems.length,
      shortTermGoalsCount: shortTermCount,
      daysWithGenericLabel: genericCount,
      dayLabels: labelsFromPlan.map((l) => `Day ${l.day}: ${l.generic ? '(generic)' : l.label.slice(0, 40)}`),
      hint: genericCount > 0 && shortTermCount < dayItems.length
        ? 'Backend should return 7 shortTermGoals (Coach goals API) OR title/description for all days in learning-paths API. See docs/COACH_LEARNING_PATH_DAY_LABELS_API.md'
        : undefined,
    });
  }

  const [resolvedSkillItems, setResolvedSkillItems] = useState<{ name: string; description?: string }[]>([]);

  const skillIdsKey = skillIdsToResolve.join(',');
  useEffect(() => {
    if (skillIdsToResolve.length === 0) return;
    let cancelled = false;
    Promise.all(
      skillIdsToResolve.map((id) =>
        skillApi.getById(id).then((skill) => ({ name: skill.name ?? id, description: skill.description }))
      )
    )
      .then((items) => {
        if (!cancelled) setResolvedSkillItems(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // skillIdsKey is stable proxy; skillIdsToResolve is new array ref each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillIdsKey]);

  const planSkillItems = initialSkillItems.length > 0 ? initialSkillItems : resolvedSkillItems;
  const profileSkillItems = profileSkills
    .map((s) => ({ name: (s.name ?? (s.skillId && !looksLikeId(s.skillId) ? s.skillId : '')).trim() }))
    .filter((s) => s.name.length > 0);
  const skillItems = [...planSkillItems];
  profileSkillItems.forEach((p) => {
    if (p.name && !skillItems.some((c) => c.name === p.name)) skillItems.push(p);
  });
  // Wins: only from backend (GET .../wins). No hardcoded or profile-derived wins — same as Learning Plan.
  const wins = activeWins?.map((w) => w.name).filter(Boolean) ?? [];

  if (import.meta.env.DEV && typeof console !== 'undefined') {
    console.log('[CoachJourneySummary] Wins (backend only):', { count: wins.length, wins: wins.slice(0, 5) });
  }

  const skillNames = skillItems.map((s) => s.name).filter(Boolean);
  const winsAndDays = getWinsAndDays(profile, learningPlan, dashboard ?? null);
  const hasLearningPlan = !!learningPlan && (!!learningPlan.title || !!learningPlan.description || skillItems.length > 0 || dayItems.length > 0);
  const hasWins = wins.length > 0;
  const hasPainPoints = painPoints.length > 0;

  // Day label: prefer lesson title from API so we never show wrong/cached/goal-based labels in Coach context
  const getDayLabel = (d: { day: number; displayLabel: string }) => {
    if (lessonTitlesByDay?.[d.day]) return lessonTitlesByDay[d.day];
    return `Day ${d.day}`;
  };

  if (!profile && !learningPlan) {
    return null;
  }

  if (compact) {
    const rawPreview = dayItems.length > 0
      ? dayItems.slice(0, 7).map((d) => d.displayLabel)
      : learningPlan?.learningModules?.[0]?.dailyContents
          ?.slice(0, 7)
          .map((d, i) => getLearningPathDayDisplayLabel(d as Record<string, unknown>, i + 1, 50))
          .filter(Boolean) ?? [];
    const planDesc = learningPlan?.description?.trim();
    const fallbackPreview = planDesc && planDesc.length > 10
      ? [planDesc.length > 50 ? `${planDesc.slice(0, 47).trim()}…` : planDesc]
      : [];
    const journeyPreview = rawPreview.length > 0 ? rawPreview : fallbackPreview;
    let journeyText = journeyPreview
      .map((l, i) => {
        const stripped = stripLearningJourneyPrefix(l);
        return isGenericLabel(stripped) ? resolveGoalLabel(stripped, i, goals) : stripped;
      })
      .filter(Boolean);
    if (journeyText.length > 0 && journeyText.every(isGenericLabel) && fallbackPreview.length > 0) {
      journeyText = fallbackPreview;
    }
    if (journeyText.length > 0 && journeyText.every(isGenericLabel) && skillNames.length >= 2) {
      journeyText = skillNames.slice(0, 3);
    }
    const journeyItems = journeyText
      .map((label, i) => ({ day: i + 1, label }))
      .filter((item) => !isGenericLabel(item.label));

    const pathItemsFromDays = dayItems.slice(0, 7).map((d) => ({ day: d.day, label: getDayLabel(d) }));
    const pathItems =
      lessonTitlesByDay != null
        ? pathItemsFromDays
        : pathItemsFromDays.some((p) => !isGenericLabel(p.label))
          ? pathItemsFromDays
          : journeyItems.length > 0
            ? journeyItems
            : (goals?.shortTermGoals ?? [])
                .slice(0, 7)
                .map((g, i) => {
                  const raw = g.title ?? g.description ?? (g as unknown as Record<string, unknown>).goal_title ?? (g as unknown as Record<string, unknown>).goal_description;
                  const label = typeof raw === 'string' ? raw.trim() : '';
                  return { day: i + 1, label };
                })
                .filter((g) => g.label);

    return (
      <div className={cn('space-y-4', className)}>
        {hasLearningPlan && learningPlan && (
          <section className="rounded-xl border border-border/70 bg-gradient-to-br from-primary/8 via-primary/3 to-transparent p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <BookOpen className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">
                  Learning path
                </h3>
                <p className="font-semibold text-sm text-foreground leading-snug">
                  {learningPlan.title && !isGenericLabel(learningPlan.title)
                    ? stripLearningJourneyPrefix(learningPlan.title)
                    : 'Your personalized path'}
                </p>
                {learningPlan.description && !isGenericLabel(learningPlan.description) && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {stripLearningJourneyPrefix(learningPlan.description)}
                  </p>
                )}
                {pathItems.length > 0 && (
                  <ul className="text-xs mt-1.5 space-y-2">
                    {pathItems.slice(0, 7).map((item) => {
                      const label = item.label.length > 50 ? `${item.label.slice(0, 47).trim()}…` : item.label;
                      const dayStatus = getDayStatus(item.day, dashboard);
                      return (
                        <li key={item.day} className="space-y-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-semibold text-primary">Day {item.day}:</span>
                                <DayStatusBadge status={dayStatus} />
                              </div>
                              <span className="block leading-snug text-foreground/90">{label}</span>
                            </div>
                            {dayStatus === 'in_progress' && onCompleteLesson && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 h-7 text-[10px] px-1.5 gap-1"
                                onClick={() => onCompleteLesson(item.day, item.label)}
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Complete
                              </Button>
                            )}
                            {dayStatus !== 'completed' && dayStatus !== 'in_progress' && onStartLesson && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 h-7 text-[10px] px-2 gap-1"
                                onClick={() => onStartLesson(item.day, item.label)}
                              >
                                <Play className="h-3 w-3" />
                                Start
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                    {pathItems.length > 7 && (
                      <li className="text-muted-foreground/80 italic pt-1">+{pathItems.length - 7} more days</li>
                    )}
                  </ul>
                )}
                {skillNames.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {skillNames.slice(0, 3).map((name, i) => (
                      <span key={i} className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                        {name}
                      </span>
                    ))}
                  </div>
                )}
                {learningPlan.estimatedDurationHours && (
                  <p className="text-xs text-primary font-medium mt-2 flex items-center gap-1.5">
                    <span>~{learningPlan.estimatedDurationHours} hrs</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-amber-600 dark:text-amber-400">{winsAndDays.label}</span>
                  </p>
                )}
              </div>
            </div>
          </section>
        )}
        <section className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/8 via-transparent to-transparent p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" />
            Wins you&apos;re chasing
          </h3>
          {hasWins ? (
            <div className="space-y-2">
              {primaryWin?.name && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2.5">
                  <p className="font-semibold text-sm text-emerald-700 dark:text-emerald-300">{primaryWin.name}</p>
                  {(primaryWin.description ?? primaryWin.summary) && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{primaryWin.description ?? primaryWin.summary}</p>
                  )}
                </div>
              )}
              <ul className="flex flex-wrap gap-2">
                {(primaryWin?.name ? wins.filter((w) => w !== primaryWin.name) : wins).map((w, i) => (
                  <li
                    key={i}
                    className="rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-3 py-1.5 text-xs font-medium border border-emerald-500/20"
                  >
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
              <Trophy className="h-8 w-8 shrink-0 text-amber-500/50" />
              <div>
                <p className="text-sm font-medium text-foreground/90">No wins data</p>
                <p className="text-xs text-muted-foreground mt-0.5">Wins come from your learning plan. When the backend has wins for you, they will appear here.</p>
              </div>
            </div>
          )}
        </section>
        {(dashboard?.overdueDayNumbers?.length ?? 0) > 0 && (
          <section className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              Overdue
            </h3>
            <p className="text-xs text-muted-foreground mb-2">Complete these to catch up?</p>
            <ul className="flex flex-wrap gap-1.5 mb-2">
              {(dashboard?.overdueDayNumbers ?? []).map((d) => (
                <li key={d}>
                  <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-1 text-[10px] font-medium border border-amber-500/20">
                    Day {d}
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/learning-plan"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
            >
              Complete in Learning Plan
              <span aria-hidden>→</span>
            </Link>
          </section>
        )}
        <section className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Pain points you&apos;re solving
              </h3>
              {hasPainPoints ? (
                <ul className="space-y-2">
                  {painPoints.slice(0, 4).map((p, i) => (
                    <li
                      key={p.id || i}
                      className="flex gap-2.5 text-sm text-foreground/90 leading-snug"
                    >
                      <span className="text-amber-500 dark:text-amber-400 shrink-0 mt-0.5 size-1.5 rounded-full bg-current" />
                      <span>{p.desc}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No pain points data. Pain points come from your profile and learning plan — they will appear here when available.</p>
              )}
            </div>
          </div>
        </section>
        <section className="rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/8 via-transparent to-transparent p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <GraduationCap className="h-3.5 w-3.5" />
            Skills you&apos;ll build
          </h3>
          {skillNames.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {skillNames.slice(0, 5).map((name, i) => (
                <span key={i} className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg bg-violet-500/5 p-3">
              <Zap className="h-8 w-8 shrink-0 text-violet-500/40" />
              <div>
                <p className="text-sm font-medium text-foreground/90">Skills we&apos;re addressing</p>
                <p className="text-xs text-muted-foreground mt-0.5">Skills from your learning path and profile will appear here.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-4 w-4" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">Your journey</h2>
      </div>

      {hasLearningPlan && learningPlan ? (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl border border-border/80 bg-gradient-to-br from-primary/5 via-background to-background p-5 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Learning path
                </h3>
                <p className="font-semibold text-foreground">
                  {learningPlan.title && !isGenericLabel(learningPlan.title)
                    ? stripLearningJourneyPrefix(learningPlan.title)
                    : 'Your personalized path'}
                </p>
                {learningPlan.description && !isGenericLabel(learningPlan.description) && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {stripLearningJourneyPrefix(learningPlan.description)}
                  </p>
                )}
              </div>
              {skillNames.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Skills you&apos;ll build</p>
                  <div className="flex flex-wrap gap-2">
                    {skillNames.map((name, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {dayItems.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    Journey overview
                  </p>
                  <ul className="space-y-3">
                    {dayItems.slice(0, 7).map((d, i) => {
                      const displayLabel = getDayLabel(d);
                      const dayStatus = getDayStatus(d.day, dashboard);
                      return (
                        <li key={i} className="space-y-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-semibold text-primary text-sm">Day {d.day}:</span>
                                <DayStatusBadge status={dayStatus} />
                              </div>
                              <span className="block text-sm text-foreground/90 leading-snug pl-0">{displayLabel}</span>
                            </div>
                            {dayStatus === 'in_progress' && onCompleteLesson && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 h-8 text-xs px-2 gap-1.5"
                                onClick={() => onCompleteLesson(d.day, displayLabel)}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Complete
                              </Button>
                            )}
                            {dayStatus !== 'completed' && dayStatus !== 'in_progress' && onStartLesson && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 h-8 text-xs px-2.5 gap-1.5"
                                onClick={() => onStartLesson(d.day, displayLabel)}
                              >
                                <Play className="h-3.5 w-3.5" />
                                Start
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                    {dayItems.length > 7 && (
                      <li className="text-xs text-muted-foreground pt-1">
                        +{dayItems.length - 7} more days
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {learningPlan.estimatedDurationHours && (
                <p className="text-xs text-primary font-medium">
                  ~{learningPlan.estimatedDurationHours} hours · {winsAndDays.label}
                </p>
              )}
            </div>
          </div>
        </motion.section>
      ) : (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-5"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Learning path
              </h3>
              <p className="text-sm text-muted-foreground italic">Your personalized path will appear here</p>
            </div>
          </div>
        </motion.section>
      )}

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-2xl border border-border/80 bg-gradient-to-br from-emerald-500/5 via-background to-background p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Target className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Wins you&apos;re chasing
            </h3>
            {hasWins ? (
              <div className="space-y-3">
                {primaryWin?.name && (
                  <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3">
                    <p className="font-semibold text-sm text-emerald-700 dark:text-emerald-300">{primaryWin.name}</p>
                    {(primaryWin.description ?? primaryWin.summary) && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{primaryWin.description ?? primaryWin.summary}</p>
                    )}
                  </div>
                )}
                <ul className="flex flex-wrap gap-2">
                  {(primaryWin?.name ? wins.filter((w) => w !== primaryWin.name) : wins).map((w, i) => (
                    <li key={i}>
                      <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-3 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                        {w}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex items-start gap-4 rounded-xl bg-amber-500/10 p-4 border border-amber-500/20">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/15">
                  <Trophy className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">No wins data</p>
                  <p className="text-sm text-muted-foreground mt-1">Wins come from your learning plan. When the backend has wins for you, they will appear here.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {(dashboard?.overdueDayNumbers?.length ?? 0) > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.04 }}
          className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-5 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Overdue
              </h3>
              <p className="text-sm text-muted-foreground mb-3">Complete these to catch up?</p>
              <ul className="flex flex-wrap gap-2 mb-3">
                {(dashboard?.overdueDayNumbers ?? []).map((d) => (
                  <li key={d}>
                    <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2.5 py-1 text-xs font-medium border border-amber-500/20">
                      Day {d}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/learning-plan"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
              >
                Complete in Learning Plan
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </motion.section>
      )}

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.08 }}
        className="rounded-2xl border border-border/80 bg-gradient-to-br from-violet-500/5 via-background to-background p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400">
            <GraduationCap className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Skills you&apos;ll build
            </h3>
            {skillNames.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {skillNames.map((name, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                  >
                    {name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex items-start gap-4 rounded-xl bg-violet-500/5 p-4 border border-violet-500/10">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-500/15">
                  <Zap className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Skills we&apos;re addressing</p>
                  <p className="text-sm text-muted-foreground mt-1">Skills from your learning path and profile will appear here.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="rounded-2xl border border-border/80 bg-gradient-to-br from-amber-500/5 via-background to-background p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Pain points you&apos;re solving
            </h3>
            {hasPainPoints ? (
              <ul className="space-y-2">
                {painPoints.map((p, i) => (
                  <li
                    key={p.id || i}
                    className="flex gap-2 text-sm text-foreground/90"
                  >
                    <span className="text-amber-500 dark:text-amber-400 mt-0.5 shrink-0">•</span>
                    <span>{p.desc}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No pain points data. Pain points come from your profile and learning plan — they will appear here when available.</p>
            )}
          </div>
        </div>
      </motion.section>
    </div>
  );
};
